import type {ImportLoading, ImportState} from '@gaia/types';
import parseFile from '@gaia/utils/gaia/parseFile';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import {gpx, kml} from '@tmcw/togeojson';
import {useEffect} from 'react';

import {useLatest} from './useLatest';

export type ReadFileOutput =
    | {type: 'geojson'; fileContents: string; filename: string}
    | {
          type: 'gpx' | 'kml' | 'kmz';
          fileContents: GeoJSON.FeatureCollection;
          filename: string;
      }
    | {type: 'fit'; fileContents: ArrayBuffer; filename: string};

function readFileAsString(file: File): Promise<string> {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
            // readAsText returns a string but Typescript doesn't know that
            resolve(reader.result as string);
        };
        reader.readAsText(file, 'utf-8');
    });
}

function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
            resolve(reader.result as ArrayBuffer);
        };
        reader.readAsArrayBuffer(file);
    });
}

async function readKMZAsString(file: File): Promise<[string | undefined, string | undefined]> {
    // https://webpack.js.org/guides/code-splitting/
    // We need `default` because Webpack does not resolve the import to the value of module.exports when
    // dynamically importing a CommonJS module. Instead it creates an artificial namespace object for the module.
    const {default: JSZip} = await import(/* webpackChunkName: "jszip" */ 'jszip');

    const kmzContent = await JSZip.loadAsync(file);

    const kmlEntry = Object.entries(kmzContent.files)
        .filter(([fileName]) => fileName.endsWith('.kml'))
        .at(0);

    if (!kmlEntry) {
        return ['Invalid KMZ - no KML found', undefined];
    }

    const [, kmlContents] = kmlEntry;

    try {
        return [undefined, await kmlContents.async('string')];
    } catch (e) {
        return ['Invalid file contents', undefined];
    }
}

async function readFile(file: File): Promise<[string, undefined] | [undefined, ReadFileOutput]> {
    const VALID_FILE_TYPES = ['geojson', 'json', 'kml', 'kmz', 'gpx', 'fit'];
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!extension || !VALID_FILE_TYPES.includes(extension)) {
        return [`The following file types are accepted: ${VALID_FILE_TYPES.join(', ')}`, undefined];
    }

    if (extension === 'json' || extension === 'geojson') {
        return [
            undefined,
            {
                type: 'geojson',
                fileContents: await readFileAsString(file),
                filename: file.name,
            },
        ];
    } else if (extension === 'gpx' || extension === 'kml' || extension === 'kmz') {
        /* We do this parsing in the main thread for a couple of reasons:
            1. DOMParser is not available within a Worker context which requires loading a large
               third-party library
            2. We cannot pass Documents from the main thread to the Worker
        */
        let contents = '';
        if (extension === 'kmz') {
            const [error, kmlString] = await readKMZAsString(file);
            if (error || !kmlString) return [error || 'Unable to read file', undefined];
            contents = kmlString;
        } else {
            contents = await readFileAsString(file);
        }

        const xml = new DOMParser().parseFromString(contents, 'text/xml');

        let geojson: GeoJSON.FeatureCollection;
        try {
            if (extension === 'kml' || extension === 'kmz') {
                geojson = kml(xml);
            } else if (extension === 'gpx') {
                geojson = gpx(xml);
            } else {
                // no-op
                return ['Unknown file type', undefined];
            }
        } catch (e) {
            console.error(e);
            return ['Invalid file contents', undefined];
        }

        return [
            undefined,
            {
                type: extension,
                fileContents: geojson,
                filename: file.name,
            },
        ];
    } else if (extension === 'fit') {
        return [
            undefined,
            {
                type: extension,
                fileContents: await readFileAsArrayBuffer(file),
                filename: file.name,
            },
        ];
    }
    // no-op
    return ['Invalid file type', undefined];
}

async function importFile(importState: ImportLoading, setImportState: SetState<ImportState>) {
    const allFeatures: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [],
    };

    for (let i = 0; i < importState.files.length; i++) {
        const [readFileError, fileOutput] = await readFile(importState.files[i]);

        if (readFileError || !fileOutput) {
            setImportState({
                status: 'selecting',
                eventSource: importState.eventSource,
                fileNames: importState.fileNames,
                error: {
                    message: 'An error occurred during the import',
                    detail: readFileError || 'No data found',
                },
            });
            return;
        }

        const [error, geojson] = await parseFile(fileOutput, window.location.origin);

        if (error || !geojson) {
            setImportState({
                status: 'selecting',
                eventSource: importState.eventSource,
                fileNames: importState.fileNames,
                error: {
                    message: 'An error occurred during the import',
                    detail: readFileError || 'No data found',
                },
            });
            return;
        }

        allFeatures.features = allFeatures.features.concat(geojson.features);
    }

    const importedFeaturesCount = allFeatures.features.reduce(
        (acc, {geometry: {type}}) => {
            if (type === 'Point') acc.waypoints++;
            else if (type === 'LineString' || type === 'MultiLineString') acc.tracks++;
            else if (type === 'Polygon') acc.areas++;
            return acc;
        },
        {waypoints: 0, tracks: 0, areas: 0}
    );

    if (allFeatures.features.length > 1000) {
        setImportState({
            status: 'selecting',
            eventSource: importState.eventSource,
            fileNames: importState.fileNames,
            error: {
                message: 'An error occurred during the import',
                detail: `You can only import 1,000 items at once. You are attempting to import ${allFeatures.features.length.toLocaleString()} items.`,
            },
        });
        ampli.importData({
            type: 'Large Import Failure',
            items: allFeatures.features.length,
            ...importedFeaturesCount,
        });
        return;
    } else if (allFeatures.features.length > 100) {
        setImportState({
            status: 'loading',
            eventSource: importState.eventSource,
            files: importState.files,
            fileNames: importState.fileNames,
            pendingImport: allFeatures,
            largeImportConfirmed: false,
        });
        ampli.importData({
            type: 'Confirm Large Import',
            items: allFeatures.features.length,
            ...importedFeaturesCount,
        });
        return;
    }

    setImportState({
        status: 'loading',
        eventSource: importState.eventSource,
        files: importState.files,
        fileNames: importState.fileNames,
        pendingImport: allFeatures,
        largeImportConfirmed: true,
        importTypes: ['waypoints', 'tracks', 'areas'],
    });
}

export function useReadFileEffect(importState: ImportState, setImportState: SetState<ImportState>) {
    const fileNames =
        (importState.status === 'loading' || importState.status === 'selecting') &&
        importState.fileNames;

    const latest = useLatest({importState, setImportState});

    useEffect(() => {
        if (latest.current.importState.status === 'loading' && latest.current.importState.files) {
            importFile(latest.current.importState, latest.current.setImportState).catch(
                console.warn
            );
        }
    }, [latest, fileNames]);
}
